


















































































































































































































































































































































































































































































































































































































































































































































import { PropType } from 'vue';
import { mapActions } from 'vuex';
import { get } from 'vuex-pathify';

import { QuoteState, Quote } from '@/models/quote';
import { Region } from '@/models/region';
import { Agent } from '@/models/agent';
import { Customer } from '@/models/customer';

import mixins from '@/utils/mixins';
import auth from '@/mixins/auth';
import { roundCurrency, setErrorFromAxiosError } from '@/utils/helpers';
import { ISystemError } from '@/models';

import SalesRepSelectorFormDialog from '../sales-rep/SalesRepSelectorFormDialog.vue';
import QuoteRFQAttachmentListItem from './QuoteRFQAttachmentListItem.vue';
import QuoteTargetDiscountDrawerListItem from './QuoteTargetDiscountDrawerListItem.vue';
import QuoteCustomerRelationshipOwnerListItem from './QuoteCustomerRelationshipOwnerListItem.vue';
import QuoteBiddingEnvironmentListItem from './QuoteBiddingEnvironmentListItem.vue';
import QuoteClassificationListItem from './QuoteClassificationListItem.vue';
import QuoteProjectSizeDisplay from './QuoteProjectSizeDisplay.vue';
import QuotePackageDisplayListItem from './QuotePackageDisplayListItem.vue';
import QuoteDocumentBuilderDialog from '@/components/quotes/QuoteDocumentBuilderDialog.vue';

const baseMixins = mixins(auth);
interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    quoteDocumentBuilderDialog: InstanceType<typeof QuoteDocumentBuilderDialog>;
  };
}

export default baseMixins
  .extend<Options>({ functional: false })
  .extend({
    name: 'QuoteDetailDrawer',

    components: {
      SalesRepSelectorFormDialog,
      QuoteRFQAttachmentListItem,
      QuoteTargetDiscountDrawerListItem,
      QuoteCustomerRelationshipOwnerListItem,
      QuoteBiddingEnvironmentListItem,
      QuoteClassificationListItem,
      QuoteProjectSizeDisplay,
      QuotePackageDisplayListItem,
      QuoteDocumentBuilderDialog
    },

    props: {
      quote: { type: Object as PropType<Quote>, required: true },
      drawer: {
        type: Boolean
      },
      total: Number,
      exchangeRate: Number,
      currencyCode: String
    },

    data() {
      return {
        agentLoading: false,
        regionLoading: false,
        ownerLoading: false,
        commissionLoading: false,
        commissionError: '',
        commentsLoading: false,
        commentsError: '',
        agentError: null as ISystemError | null,
        /** LF --2023-06-12 - Removing ability to edit owner. This does not work because only admins (is_staff) can get list of owners to select from */
        canEditOwner: false,

        commissionPercent: this.quote.commission_percent
      };
    },

    watch: {
      'quote.commission_percent': {
        handler(value: number) {
          this.commissionPercent = value;
        },
        immediate: true
      }
    },

    computed: {
      getRegionById: get<Region>('region/getRegionById'),
      getAgentById: get<Agent>('agent/getAgentById'),
      getCustomerById: get<(id: string) => Customer>(
        'customer/getCustomerById'
      ),

      quoteDrawer: {
        get(): boolean {
          return this.drawer;
        },
        set(drawer: boolean) {
          this.$emit('update:drawer', drawer);
        }
      },

      isEditable(): boolean {
        switch (this.quote.state) {
          case QuoteState.DRAFT:
          case QuoteState.SUBMITTED:
          case QuoteState.IN_REVIEW:
            return true;
          default:
            return false;
        }
      }
    },

    mounted() {
      this.fetchAgents();
      this.fetchRegions();
    },

    methods: {
      ...mapActions({
        setComments: 'quote/setComments',
        setQuoteOwner: 'quote/setQuoteOwner',
        setQuoteCommissionPercent: 'quote/setQuoteCommissionPercent',
        setQuoteRegion: 'quote/setQuoteRegion',
        setQuoteAgent: 'quote/setQuoteAgent',

        fetchAgents: 'agent/fetchAgents',

        fetchRegions: 'region/fetchRegions'
      }),

      buildQuoteDocument() {
        this.$refs.quoteDocumentBuilderDialog.open(this.quote);
      },

      setCommissionPercent(commission_percent: number) {
        this.commissionLoading = true;

        this.setQuoteCommissionPercent({
          id: this.quote.number,
          commission_percent
        })
          .then(() => {
            (this.$refs.commission as any).save(commission_percent);
            this.$emit('change:commission');
          })
          .catch(error => {
            this.commissionError = 'Unable to update commission for quote';
            if (error.response) {
              if (error.response.data.detail) {
                this.commissionError = error.response.data.detail;
              }
            }
          })
          .finally(() => {
            this.commissionLoading = false;
          });
      },

      addComments(comments: string) {
        this.commentsLoading = true;
        this.commentsError = '';

        this.setComments({
          id: this.quote.number,
          comments
        })
          .then(() => {
            this.triggerChange();
          })
          .catch(() => {
            this.commentsError = 'Error while attempting to submit comments.';
          })
          .finally(() => {
            this.commentsLoading = false;
          });
      },

      setInsideSalesOwner(insideSalesOwner: string) {
        this.quote.inside_sales_owner = insideSalesOwner;
        this.triggerChange();
      },

      setRegion(region: string) {
        this.regionLoading = true;
        this.setQuoteRegion({ id: this.quote.number, region })
          .then(() => {
            this.triggerChange();
            (this.$refs.region as any).save(region);
          })
          .catch(() => {
            (this.$refs.region as any).isActive = false;
          })
          .finally(() => {
            this.regionLoading = false;
          });
      },

      setOwner(owner: number) {
        this.ownerLoading = true;
        this.setQuoteOwner({ id: this.quote.number, user: owner })
          .then(() => {
            this.triggerChange();
            (this.$refs.owner as any).save(owner);
          })
          .catch(() => {
            (this.$refs.owner as any).isActive = false;
          })
          .finally(() => {
            this.ownerLoading = false;
          });
      },

      setAgent(agent: number) {
        this.agentLoading = true;
        this.agentError = null;
        this.setQuoteAgent({ id: this.quote.number, user: agent })
          .then(() => {
            (this.$refs.agent as any).save(agent);
            this.triggerChange();
          })
          .catch(err => {
            this.agentError = setErrorFromAxiosError(err);
          })
          .finally(() => {
            this.agentLoading = false;
          });
      },

      triggerChange() {
        this.$emit('change');
      },

      getStatusText(state: number) {
        switch (state) {
          case QuoteState.DRAFT:
            return 'Draft';

          case QuoteState.SUBMITTED:
            return 'Submitted';

          case QuoteState.IN_REVIEW:
            return 'In Review';

          case QuoteState.APPROVAL_2_PENDING:
            return 'Approval II Pending';

          case QuoteState.APPROVAL_3_PENDING:
            return 'Approval III Pending';

          case QuoteState.APPROVAL_4_PENDING:
            return 'Approval IV Pending';

          case QuoteState.APPROVED:
            return 'Approved';

          case QuoteState.PREPARE_FOR_CUSTOMER:
            return 'Ready to Share';

          case QuoteState.OUT_TO_CUSTOMER:
            return 'Out to Customer';

          case QuoteState.AWARDED:
            return 'Accepted';

          case QuoteState.LOST:
            return 'Lost';

          case QuoteState.CANCELLED:
            return 'Cancelled';

          case QuoteState.ORDER_CREATED:
            return 'Order Created';

          case QuoteState.REVIEWING_DISCOUNTS:
            return 'Reviewing Discounts';

          default:
            return 'Error';
        }
      },

      showCustomerTypeLabel(customer_identifier: string) {
        const customer = this.getCustomerById(customer_identifier);
        return customer.type
          ? `${customer.type} - ${customer.type_description}`
          : 'None';
      },

      /**
       * This is used in multiple places but the function's name in invalid for its current use case
       * TODO: LH - 2024-03-07 - Refactor function name to be more generic
       */
      roundedPercentage: roundCurrency
    }
  });
